import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, MenuItem, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import QuizIcon from '@mui/icons-material/Quiz';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { lmsService } from '../../services/api';
import LmsRichEditor from '../../components/lms/LmsRichEditor';
import LmsVersionHistory from '../../components/lms/LmsVersionHistory';
import { sanitizeCourseHtml } from '../../components/lms/sanitizeHtml';

const emptyForm = { source: 'База знань', base: '', module: '', lesson: '', title: '', body: '' };
const KB_ACCEPT = [
  '.txt', '.md', '.csv', '.html', '.htm', '.rtf', '.pdf', '.docx',
  '.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp',
  '.mp4', '.webm', '.mov', '.m4v',
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  'video/mp4', 'video/webm', 'video/*',
].join(',');

export default function LmsMaterialsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [source, setSource] = useState('');
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingRecId, setEditingRecId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, qRes] = await Promise.all([
        lmsService.materials(source ? { source } : {}),
        lmsService.quizzes().catch(() => ({ data: [] })),
      ]);
      setRows(Array.isArray(mRes.data) ? mRes.data : []);
      setQuizzes(Array.isArray(qRes.data) ? qRes.data : qRes.data?.results || []);
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [source]);

  useEffect(() => { load(); }, [load]);

  const quizzesByMaterial = useMemo(() => {
    const map = {};
    for (const q of quizzes) {
      if (!q.material) continue;
      if (!map[q.material]) map[q.material] = [];
      map[q.material].push(q);
    }
    return map;
  }, [quizzes]);

  const openCreate = () => {
    setEditingId(null);
    setEditingRecId(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setEditingRecId(row.rec_id || null);
    setForm({
      source: row.source || 'База знань',
      base: row.base || '',
      module: row.module || '',
      lesson: row.lesson || '',
      title: row.title || '',
      body: row.body || '',
    });
    setOpen(true);
  };

  const handleSave = async () => {
    if (saving) return;
    if (!(form.title || '').trim()) { alert('Вкажіть назву'); return; }
    setSaving(true);
    try {
      const payload = { ...form, lesson: form.lesson || form.title };
      if (editingId) await lmsService.materialUpdate(editingId, payload);
      else await lmsService.materialCreate(payload);
      setOpen(false);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleKbFiles = async (ev) => {
    const list = Array.from(ev.target.files || []);
    if (fileRef.current) fileRef.current.value = '';
    if (!list.length) return;
    setUploading(true);
    setUploadMsg('');
    try {
      const res = await lmsService.materialUploadKb(list, { source: 'База знань' });
      const n = res.data?.count || 0;
      const errs = res.data?.errors || [];
      let msg = `Завантажено в базу знань: ${n}`;
      if (errs.length) {
        msg += `. Помилки: ${errs.map((e) => `${e.file}: ${e.detail}`).join('; ')}`;
      }
      setUploadMsg(msg);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MenuBookIcon color="primary" />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Матеріали</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TextField select size="small" value={source} onChange={(e) => setSource(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">Усі</MenuItem>
            <MenuItem value="Навчання">Навчання</MenuItem>
            <MenuItem value="База знань">База знань</MenuItem>
          </TextField>
          <input
            ref={fileRef}
            type="file"
            accept={KB_ACCEPT}
            multiple
            hidden
            onChange={handleKbFiles}
          />
          <Button
            variant="outlined"
            startIcon={uploading ? <CircularProgress size={18} /> : <UploadFileIcon />}
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
          >
            У базу знань
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Додати</Button>
        </Box>
      </Box>
      {uploadMsg ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setUploadMsg('')}>{uploadMsg}</Alert>
      ) : null}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        У базу знань: документи (txt/md/pdf/docx… до 25 МБ, текст для AI), зображення (jpg/png… до 25 МБ), відео (mp4/webm… до 200 МБ).
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Назва</TableCell>
                <TableCell>Джерело</TableCell>
                <TableCell>Розділ</TableCell>
                <TableCell align="right">Дії</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography color="text.secondary">Матеріалів ще немає.</Typography>
                  </TableCell>
                </TableRow>
              ) : rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    {row.title}
                    {(quizzesByMaterial[row.id] || []).length ? (
                      <Chip size="small" sx={{ ml: 1 }} label={`тест: ${quizzesByMaterial[row.id].length}`} />
                    ) : null}
                  </TableCell>
                  <TableCell>{row.source}</TableCell>
                  <TableCell>{row.base || '—'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" title="Переглянути" onClick={() => setViewRow(row)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => openEdit(row)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={async () => {
                        if (!window.confirm('Видалити?')) return;
                        await lmsService.materialDelete(row.id);
                        load();
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={Boolean(viewRow)} onClose={() => setViewRow(null)} maxWidth="md" fullWidth>
        <DialogTitle>{viewRow?.title || 'Матеріал'}</DialogTitle>
        <DialogContent>
          {viewRow?.body ? (
            <Box
              className="lms-rich-root coursehtml"
              sx={{ mb: 2 }}
              dangerouslySetInnerHTML={{ __html: sanitizeCourseHtml(viewRow.body) }}
            />
          ) : (
            <Typography color="text.secondary" sx={{ mb: 2 }}>Порожній матеріал.</Typography>
          )}
          {(quizzesByMaterial[viewRow?.id] || []).length > 0 ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>Тест наприкінці уроку</Typography>
              {(quizzesByMaterial[viewRow.id] || []).filter((q) => q.published).map((q) => (
                <Box key={q.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Typography sx={{ flex: 1 }}>{q.title}</Typography>
                  {q.settings?.pass_mark != null ? (
                    <Chip size="small" label={`поріг ${q.settings.pass_mark}%`} variant="outlined" />
                  ) : null}
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<QuizIcon />}
                    onClick={() => navigate(`/lms/tests/${q.id}`)}
                  >
                    Пройти тест
                  </Button>
                </Box>
              ))}
            </Paper>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewRow(null)}>Закрити</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={open}
        onClose={() => !saving && setOpen(false)}
        maxWidth="md"
        fullWidth
        disableEnforceFocus
        disableAutoFocus
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            <span>{editingId ? 'Редагувати матеріал' : 'Новий матеріал'}</span>
            {editingRecId ? (
              <LmsVersionHistory
                entityType="material"
                entityId={editingRecId}
                onRestored={() => load()}
              />
            ) : null}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField select label="Джерело" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
              <MenuItem value="Навчання">Навчання</MenuItem>
              <MenuItem value="База знань">База знань</MenuItem>
            </TextField>
            <TextField label="Розділ" value={form.base} onChange={(e) => setForm({ ...form, base: e.target.value })} fullWidth />
            <TextField label="Модуль" value={form.module} onChange={(e) => setForm({ ...form, module: e.target.value })} fullWidth />
            <TextField label="Назва" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} fullWidth required />
            <LmsRichEditor
              label="Контент"
              value={form.body}
              onChange={(body) => setForm({ ...form, body })}
              minHeight={240}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Скасувати</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
